import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CardContent from '@material-ui/core/CardContent';
import CloseIcon from '@material-ui/icons/Close';
import Enumerable from 'linq';
import * as validator from 'email-validator';
import { EmailAddressCell } from '../SubComponents/EmailAddressCell';
import { EventClusterRoleDropdownCell } from '../SubComponents/EventClusterRoleDropdownCell';
import { InviteUserActionCell } from '../SubComponents/InviteUserActionCell';
import { GenericSuccessModal } from '../GenericSuccessModal';
import { GenericErrorModal } from '../GenericErrorModal';
import { AddRoleDialog } from './AddRoleDialog';
import { getEventClusterRoles } from '../../../data/EventClusterRoleRepository';
import { createEventClusterInvites } from '../../../data/EventClusterInviteRepository';
import { getAllEventClusters } from '../../../data/EventClusterRepository';
import { GenericDropdown } from '../SubComponents/GenericDropdown';
import { EventClusterPermissionCode } from "../../../data/Models/EventClusterPermissionCode";

interface EventClusterInvitesModalProps {
  open: boolean;

  // app scaffolding
  loadingOn: () => void;
  loadingOff: () => void;
  changePage: (url: string) => void;

  // lifecycle
  onClose: () => void;
}

interface InviteRow {
  email: string;
  eventClusterRoleId: string;
  emailError?: string;
  roleError?: string;
}

export const EventClusterInvitesModal: React.FC<EventClusterInvitesModalProps> = ({
  open,
  loadingOn,
  loadingOff,
  changePage,
  onClose,
}) => {
  const [eventClusters, setEventClusters] = useState<any[]>([]);
  const [currentEventClusterId, setCurrentEventClusterId] = useState<string>('');
  const [addRolesAccess, setAddRolesAccess] = useState<boolean>(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [insertEventClusterInvites, setInsertEventClusterInvites] = useState<InviteRow[]>([]);
  const [focusedId, setFocusedId] = useState<string>('');
  const [serverErrorText, setServerErrorText] = useState<string>('');
  const [serverSuccessMsg, setServerSuccessMsg] = useState<string>('');
  const [newRoleModalOpen, setNewRoleModalOpen] = useState<boolean>(false);

  const headCells = useMemo(
    () => [
      { id: 'email', label: 'Email' },
      { id: 'role', label: 'Role' },
    ],
    []
  );

  // keep latest function props in refs to avoid effect thrashing
  const loadingOnRef = useRef(loadingOn);
  const loadingOffRef = useRef(loadingOff);
  const changePageRef = useRef(changePage);

  useEffect(() => {
    loadingOnRef.current = loadingOn;
    loadingOffRef.current = loadingOff;
    changePageRef.current = changePage;
  }, [loadingOn, loadingOff, changePage]);

  // Load accessible event clusters when dialog opens
  useEffect(() => {
    let isMounted = true;

    const loadEventClusters = async () => {
      // guard: only run while mounted and dialog is still open
      if (!isMounted || !open) return;

      loadingOnRef.current();
      let eventClusterInviteServerError = '';

      const resp = await getAllEventClusters();
      if (resp != null && resp.status == 401) {
        eventClusterInviteServerError = 'You login session has expired. Please login again.';
      } else if (resp != null && resp.status == 200) {
        if (!resp.data.success) {
          eventClusterInviteServerError = resp.data.errorMsg;
          if (isMounted) setEventClusters([]);
        } else {
          const list = Array.isArray(resp?.data?.eventClusters) ? resp.data.eventClusters : [];
          const clusters = list.filter((c: any) => {
            const isOwner = !!c?.role?.isOwnerRole;
            const perms = Array.isArray(c?.role?.permissions) ? c.role.permissions : [];
            const canInvite = perms.some((p: any) => p?.id === EventClusterPermissionCode.InviteUsers);
            return isOwner || canInvite;
          });
          if (isMounted) setEventClusters(clusters || []);
        }
      } else {
        eventClusterInviteServerError = 'An unknown error occured. Please try again later';
      }

      if (isMounted) setServerErrorText(eventClusterInviteServerError);
      loadingOffRef.current();
    };

    if (open) {
      // reset local state on open once
      setInsertEventClusterInvites([]);
      setServerErrorText('');
      setServerSuccessMsg('');
      loadEventClusters();
    }

    return () => {
      isMounted = false;
    };
  }, [open]); // depend only on `open`

  const AddInviteUserClick = () => {
    setInsertEventClusterInvites((prev) => [...prev, { email: '', eventClusterRoleId: '', emailError: '', roleError: '' }]);
  };

  const OnEmailChange = (dataIndex: number, value: any, id: string) => {
    setInsertEventClusterInvites((prev) => {
      const next = [...prev];
      next[dataIndex] = { ...next[dataIndex], email: value };
      return next;
    });
    setFocusedId(id);
  };

  const OnRoleDropdownChange = (dataIndex: number, value: any) => {
    setInsertEventClusterInvites((prev) => {
      const next = [...prev];
      next[dataIndex] = { ...next[dataIndex], eventClusterRoleId: value };
      return next;
    });
  };

  const RemoveFocusedId = () => setFocusedId('');

  const DeleteInviteUser = (dataIndex: number) => {
    setInsertEventClusterInvites((prev) => prev.filter((_, i) => i !== dataIndex));
  };

  const CloseSuccessModal = () => setServerSuccessMsg('');
  const CloseErrorModal = () => setServerErrorText('');

  const OpenNewRoleModal = () => setNewRoleModalOpen(true);
  const CloseNewRoleModal = () => setNewRoleModalOpen(false);
  const OnRoleAdded = async () => {
    setNewRoleModalOpen(false);
    setServerSuccessMsg('Role added successfully.');
    // reload roles
    await GetEventClusterRoles(currentEventClusterId);
  };

  const GetEventClusterRoles = async (eventClusterId: string) => {
    loadingOn();
    let eventClusterInviteServerError = '';
    const resp = await getEventClusterRoles(eventClusterId);
    if (resp != null && resp.status == 401) {
      eventClusterInviteServerError = 'You login session has expired. Please login again.';
    } else if (resp != null && resp.status == 200) {
      if (!resp.data.success) {
        eventClusterInviteServerError = resp.data.errorMsg;
        setRoles([]);
      } else {
        var eventClusterRoles = resp.data.eventClusterRoles?.filter((r: any) => !r.isOwnerRole);
        setRoles(eventClusterRoles || []);
      }
    } else {
      eventClusterInviteServerError = 'An unknown error occured. Please try again later';
    }
    setServerErrorText(eventClusterInviteServerError);
    loadingOff();
  }


  const CreateEventClusterInviteClick = async () => {
    loadingOn();

    let errored = false;
    const newInsertRows: InviteRow[] = [];

    const requestInsertEventClusterInvites: { email: string; eventClusterRoleId: string }[] = [];

    for (let i = 0; i < insertEventClusterInvites.length; i++) {
      const row = { ...insertEventClusterInvites[i], emailError: '', roleError: '' };

      if (row.email.length === 0) {
        row.emailError = 'Email required.';
        errored = true;
      } else if (validator.validate(row.email) === false) {
        row.emailError = `"${row.email}" is not a valid email address.`;
        errored = true;
      } else {
        const emailCount = Enumerable.from(insertEventClusterInvites).where((r: any) => r.email === row.email).count();
        if (emailCount > 1) {
          row.emailError = `"${row.email}" is duplicated in the invites list.`;
          errored = true;
        }
      }

      if (row.eventClusterRoleId === '') {
        row.roleError = 'You must make a role selection';
        errored = true;
      }

      newInsertRows.push(row);

      requestInsertEventClusterInvites.push({
        email: row.email,
        eventClusterRoleId: row.eventClusterRoleId,
      });
    }

    if (!errored) {
      const request = {
        eventClusterId: currentEventClusterId,
        eventClusterInvites: requestInsertEventClusterInvites,
      };

      const response = await createEventClusterInvites(request);

      if (response != null && response.status == 401) {
        setServerErrorText('You login session has expired. Please login again.');
      } else if (response != null && response.status == 200) {
        if (!response.data.success) {
          setServerErrorText(response.data.errorMsg || 'Failed to send invites.');
        } else {
          setServerSuccessMsg('Invites sent successfully.');
          setInsertEventClusterInvites([]);
        }
      } else {
        setServerErrorText('An unknown error occured. Please try again later');
      }
    }

    setInsertEventClusterInvites(newInsertRows);
    loadingOff();
  };

  const HandleEventCluserSelected = async (eventClusterId: string) => {
    setCurrentEventClusterId(eventClusterId);

    const selectedCluster = eventClusters.find((ec) => ec.eventClusterId === eventClusterId);
    setAddRolesAccess(
      selectedCluster.role.isOwnerRole ||
        selectedCluster.role.permissions.some((p: any) => p.id === EventClusterPermissionCode.AddRoles)
    );
    // load roles for event cluster
    await GetEventClusterRoles(eventClusterId);
  }

  return (
    <>
      <Dialog
        onClose={onClose}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
        open={open}
      >
        <DialogTitle disableTypography className="dialog-title-warning">
          <Typography variant="h6">Invite Users</Typography>
          <IconButton aria-label="close" className="close-button" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <CardContent>
            <Box style={{ marginBottom: '20px' }}>
              <GenericDropdown 
                  label="Select Event Group"
                  value={currentEventClusterId}
                  data={eventClusters.map((ec) => ({ id: ec.eventClusterId, name: ec.eventClusterName }))}
                  onChange={HandleEventCluserSelected}
              />     
            </Box>
            {addRolesAccess && (
              <Button
                variant="contained"
                className="btn-secondary"
                onClick={OpenNewRoleModal}
                style={{ marginBottom: '40px' }}
              >
                Add New Role
              </Button>
            )}
            <div style={{ padding: '0px 0px 40px 0px' }}>
              <Button variant="contained" className="btn-secondary" onClick={AddInviteUserClick}>
                Add User Invite
              </Button>
            </div>
            <Box style={{ width: '100%' }}>
              <Paper style={{ width: '100%' }}>
                <TableContainer>
                  <Table
                    aria-labelledby="tableTitle"
                    size="medium"
                    style={{ marginLeft: '0.5%', width: '99%' }}
                    className="new-event-cluster-invites-table table-filters-hidden"
                  >
                    <TableHead>
                      <TableRow>
                        {headCells.map((headCell) => (
                          <TableCell key={headCell.id} className="table-header-border show-on-mobile">
                            {headCell.label}
                          </TableCell>
                        ))}
                        <TableCell className="table-header-border no-show-on-mobile"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {insertEventClusterInvites.map((row: InviteRow, index: number) => (
                        <TableRow hover tabIndex={-1} key={`invite-row-${index}`}>
                          <TableCell align="right">
                            <EmailAddressCell
                              dataItem={row}
                              dataIndex={index}
                              focusedId={focusedId}
                              onChange={OnEmailChange}
                              removeFocusedId={RemoveFocusedId}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <EventClusterRoleDropdownCell
                              title="Role"
                              dataItem={row}
                              dataIndex={index}
                              onChange={OnRoleDropdownChange}
                              roles={roles}
                              editMode={true}
                            />
                          </TableCell>
                          <TableCell>
                            <InviteUserActionCell dataIndex={index} deleteInviteUser={DeleteInviteUser} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          </CardContent>
        </DialogContent>
        <DialogActions disableSpacing={true}>
          <Button onClick={onClose} className="two-action-buttons">
            Close
          </Button>
          <Button onClick={CreateEventClusterInviteClick} className="two-action-buttons">
            Send Invites
          </Button>
        </DialogActions>
      </Dialog>

      <GenericSuccessModal hidden={serverSuccessMsg == ''} message={serverSuccessMsg} closeModal={CloseSuccessModal} />
      <GenericErrorModal hidden={serverErrorText == ''} message={serverErrorText} closeModal={CloseErrorModal} />

      {newRoleModalOpen && <AddRoleDialog
        visible={newRoleModalOpen}
        eventClusterId={currentEventClusterId}
        loadingOff={loadingOff}
        loadingOn={loadingOn}
        changePage={changePage}
        onCloseDialog={CloseNewRoleModal}
        onRoleAdded={OnRoleAdded}
      />}
    </>
  );
};